import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const PROJECT_STATUS_OPTIONS = ['planning', 'not_started', 'in_progress', 'active', 'on_hold', 'delayed', 'completed'];
const TASK_STATUS_OPTIONS = ['not_started', 'in_progress', 'completed'];

const PROJECT_STATUS_LABELS = {
  planning: 'Planning',
  not_started: 'Not Started',
  in_progress: 'In Progress',
  active: 'Active',
  on_hold: 'On Hold',
  delayed: 'Delayed',
  completed: 'Completed',
};

const TASK_STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const makeProjectDraft = () => ({
  title: '',
  description: '',
  start_date: '',
  end_date: '',
  priority: 'medium',
  status: 'planning',
});

const makeTaskDraft = () => ({
  name: '',
  notes: '',
  start_date: '',
  end_date: '',
  status: 'not_started',
  progress_percent: 0,
});

const formatDate = (value) => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeTaskStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'pending') return 'not_started';
  return normalized || 'not_started';
};

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function ProjectStat({ label, value, tone = 'default' }) {
  return (
    <article className={`projects-stat projects-stat--${tone}`}>
      <span className="projects-stat-label">{label}</span>
      <strong className="projects-stat-value">{value}</strong>
    </article>
  );
}

function ProjectField({ label, children }) {
  return (
    <label className="projects-field">
      <span className="projects-field-label">{label}</span>
      {children}
    </label>
  );
}

function ProjectListItem({ project, active, onSelect }) {
  return (
    <button
      type="button"
      className={`projects-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(project.id)}
    >
      <div className="projects-list-topline">
        <strong className="projects-list-title">{project.title || 'Untitled project'}</strong>
        <span className={`projects-status-badge projects-status-badge--${String(project.status || '').toLowerCase()}`}>
          {PROJECT_STATUS_LABELS[project.status] || project.status || 'Planning'}
        </span>
      </div>
      <div className="projects-list-meta">
        <span>{project.priority || 'medium'} priority</span>
        <span>{project.taskCount || 0} tasks</span>
        <span>{project.overall_progress || 0}% progress</span>
      </div>
    </button>
  );
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageProjects = hasFrontendPermission(user, 'projects.manage');
  const canManageTasks = hasFrontendPermission(user, 'tasks.manage');

  const [projects, setProjects] = useState([]);
  const [tasksByProject, setTasksByProject] = useState({});
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [projectDraft, setProjectDraft] = useState(makeProjectDraft);
  const [taskDraft, setTaskDraft] = useState(makeTaskDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadProjects = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/projects', { token });
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadTasks = useCallback(async (projectId) => {
    if (!token || !projectId) return;
    try {
      const data = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/tasks`, { token });
      setTasksByProject((prev) => ({ ...prev, [projectId]: Array.isArray(data) ? data : [] }));
    } catch (_e) {
      // Keep page usable if one task fetch fails.
    }
  }, [token]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects, refreshKey]);

  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((project) => (
      [project.title, project.description, project.priority, project.status]
        .some((value) => String(value || '').toLowerCase().includes(term))
    ));
  }, [projects, searchTerm]);

  useEffect(() => {
    if (!filteredProjects.length) {
      setSelectedProjectId('');
      return;
    }
    if (!filteredProjects.some((project) => project.id === selectedProjectId)) {
      setSelectedProjectId(filteredProjects[0].id);
    }
  }, [filteredProjects, selectedProjectId]);

  const selectedProject = useMemo(
    () => filteredProjects.find((project) => project.id === selectedProjectId) || null,
    [filteredProjects, selectedProjectId],
  );

  useEffect(() => {
    if (selectedProject?.id && !tasksByProject[selectedProject.id]) {
      loadTasks(selectedProject.id);
    }
  }, [selectedProject, tasksByProject, loadTasks]);

  const selectedTasks = selectedProject ? (tasksByProject[selectedProject.id] || []) : [];

  const stats = useMemo(() => ({
    total: projects.length,
    active: projects.filter((project) => ['planning', 'not_started', 'in_progress', 'active'].includes(project.status)).length,
    completed: projects.filter((project) => project.status === 'completed').length,
    delayed: projects.filter((project) => project.status === 'delayed').length,
  }), [projects]);
  const quickActions = useMemo(() => {
    const actions = [];
    if (canManageProjects) {
      actions.push({
        label: showProjectForm ? 'Hide Project Form' : 'Create Project',
        onClick: () => setShowProjectForm((value) => !value),
      });
    }
    if (selectedProject && canManageTasks) {
      actions.push({
        label: showTaskForm ? 'Hide Task Form' : 'Add Task',
        onClick: () => setShowTaskForm((value) => !value),
      });
    }
    if (stats.delayed > 0) {
      actions.push({
        label: 'Review Delayed',
        onClick: () => {
          const delayedProject = projects.find((project) => project.status === 'delayed');
          if (delayedProject) setSelectedProjectId(delayedProject.id);
        },
      });
    }
    return actions.slice(0, 3);
  }, [canManageProjects, canManageTasks, showProjectForm, showTaskForm, selectedProject, stats.delayed, projects]);

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleCreateProject = async (event) => {
    event.preventDefault();
    if (!token || !canManageProjects) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/projects', {
        token,
        method: 'POST',
        body: projectDraft,
      });
      setProjects((prev) => [created, ...prev]);
      setSelectedProjectId(created.id);
      setProjectDraft(makeProjectDraft());
      setShowProjectForm(false);
      pushToast(`Created ${created.title}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to create project');
      pushToast(e.message || 'Failed to create project', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTask = async (event) => {
    event.preventDefault();
    if (!token || !canManageTasks || !selectedProject) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/tasks', {
        token,
        method: 'POST',
        body: {
          project_id: selectedProject.id,
          name: taskDraft.name,
          notes: taskDraft.notes || '',
          start_date: taskDraft.start_date || null,
          end_date: taskDraft.end_date || null,
          status: normalizeTaskStatus(taskDraft.status),
          progress_percent: Number(taskDraft.progress_percent || 0),
          weight: 1,
        },
      });
      setTasksByProject((prev) => ({
        ...prev,
        [selectedProject.id]: [...(prev[selectedProject.id] || []), created],
      }));
      setTaskDraft(makeTaskDraft());
      setShowTaskForm(false);
      pushToast(`Added task to ${selectedProject.title}`, 'success');
      setRefreshKey((value) => value + 1);
    } catch (e) {
      setError(e.message || 'Failed to create task');
      pushToast(e.message || 'Failed to create task', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTaskProgress = async (task, nextProgress) => {
    if (!token || !canManageTasks) return;
    setSaving(true);
    setError('');
    try {
      const status = nextProgress >= 100 ? 'completed' : nextProgress > 0 ? 'in_progress' : 'not_started';
      const updated = await apiFetch(`/api/tasks/${encodeURIComponent(task.id)}`, {
        token,
        method: 'PUT',
        body: {
          progress_percent: nextProgress,
          status,
        },
      });
      setTasksByProject((prev) => ({
        ...prev,
        [selectedProject.id]: (prev[selectedProject.id] || []).map((item) => item.id === updated.id ? updated : item),
      }));
      setRefreshKey((value) => value + 1);
    } catch (e) {
      setError(e.message || 'Failed to update task');
      pushToast(e.message || 'Failed to update task', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="projects-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="projects-page">
      <div className="projects-header">
        <div>
          <span className="projects-eyebrow">Delivery</span>
          <h1 className="projects-heading">Projects</h1>
          <p className="projects-subheading">
            Portfolio status, project timelines, and task execution in a single planning workspace.
          </p>
        </div>
        <div className="projects-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
          {canManageProjects ? (
            <Button onClick={() => setShowProjectForm((value) => !value)}>
              <PlusIcon />
              New Project
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadProjects} /> : null}

      <Card className="workflow-guide-card" title="What To Do Here">
        <div className="workflow-guide-head">
          <p className="workflow-guide-copy">
            Use Projects to track delivery health, keep scope visible, and turn project goals into clear next tasks.
          </p>
          <div className="workflow-guide-actions">
            {quickActions.map((action) => (
              <Button key={action.label} variant="secondary" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="workflow-guide-list">
          <div className="workflow-guide-item">Open the delayed or active portfolio items first to understand where delivery is slipping.</div>
          <div className="workflow-guide-item">Use the project detail panel to review dates, progress, and task volume before adding work.</div>
          <div className="workflow-guide-item">Break projects into tasks early so the planner and team pages have something actionable to work with.</div>
        </div>
      </Card>

      <div className="projects-stats-grid">
        <ProjectStat label="Total" value={stats.total} />
        <ProjectStat label="Active" value={stats.active} tone="accent" />
        <ProjectStat label="Completed" value={stats.completed} tone="info" />
        <ProjectStat label="Delayed" value={stats.delayed} tone="danger" />
      </div>

      {showProjectForm ? (
        <Card className="projects-create-card" title="Create Project">
          <form className="projects-form-grid" onSubmit={handleCreateProject}>
            <ProjectField label="Title">
              <input className="projects-input" value={projectDraft.title} onChange={(event) => setProjectDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Warehouse retrofit rollout" required />
            </ProjectField>
            <ProjectField label="Priority">
              <select className="projects-input" value={projectDraft.priority} onChange={(event) => setProjectDraft((prev) => ({ ...prev, priority: event.target.value }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </ProjectField>
            <ProjectField label="Start Date">
              <input className="projects-input" type="date" value={projectDraft.start_date} onChange={(event) => setProjectDraft((prev) => ({ ...prev, start_date: event.target.value }))} />
            </ProjectField>
            <ProjectField label="Deadline">
              <input className="projects-input" type="date" value={projectDraft.end_date} onChange={(event) => setProjectDraft((prev) => ({ ...prev, end_date: event.target.value }))} />
            </ProjectField>
            <ProjectField label="Description">
              <textarea className="projects-input projects-textarea" value={projectDraft.description} onChange={(event) => setProjectDraft((prev) => ({ ...prev, description: event.target.value }))} placeholder="Scope, milestones, customer context" />
            </ProjectField>
            <div className="projects-form-actions">
              <Button variant="ghost" onClick={() => setShowProjectForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>Create Project</Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="projects-layout">
        <Card className="projects-list-card" title="Portfolio">
          <div className="projects-toolbar">
            <input className="projects-input" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by title, status, priority, or description" />
          </div>

          {filteredProjects.length ? (
            <div className="projects-list">
              {filteredProjects.map((project) => (
                <ProjectListItem key={project.id} project={project} active={project.id === selectedProjectId} onSelect={setSelectedProjectId} />
              ))}
            </div>
          ) : (
            <EmptyState title="No projects in this view" message="Create a new project or widen the search scope." />
          )}
        </Card>

        <Card className="projects-detail-card" title={selectedProject ? selectedProject.title : 'Details'}>
          {!selectedProject ? (
            <EmptyState title="No project selected" message="Pick a project from the portfolio to review scope and tasks." />
          ) : (
            <div className="projects-detail">
              <div className="projects-detail-head">
                <div className="projects-detail-topline">
                  <span className={`projects-status-badge projects-status-badge--${String(selectedProject.status || '').toLowerCase()}`}>
                    {PROJECT_STATUS_LABELS[selectedProject.status] || selectedProject.status || 'Planning'}
                  </span>
                  <span className={`projects-priority-badge projects-priority-badge--${String(selectedProject.priority || 'medium').toLowerCase()}`}>
                    {selectedProject.priority || 'medium'}
                  </span>
                </div>
                <p className="projects-detail-copy">
                  {selectedProject.description || 'No project description captured yet.'}
                </p>
              </div>

              <div className="projects-detail-grid">
                <ProjectField label="Start">
                  <div className="projects-readout">{formatDate(selectedProject.start_date)}</div>
                </ProjectField>
                <ProjectField label="Deadline">
                  <div className="projects-readout">{formatDate(selectedProject.end_date)}</div>
                </ProjectField>
                <ProjectField label="Progress">
                  <div className="projects-readout">{selectedProject.overall_progress || 0}% complete</div>
                </ProjectField>
                <ProjectField label="Tasks">
                  <div className="projects-readout">{selectedProject.taskCount || selectedTasks.length || 0} total</div>
                </ProjectField>
              </div>

              <div className="projects-tasks-head">
                <h3 className="projects-tasks-title">Tasks</h3>
                {canManageTasks ? (
                  <Button size="sm" variant="secondary" onClick={() => setShowTaskForm((value) => !value)}>
                    <PlusIcon />
                    Add Task
                  </Button>
                ) : null}
              </div>

              {showTaskForm ? (
                <form className="projects-task-form" onSubmit={handleCreateTask}>
                  <div className="projects-form-grid">
                    <ProjectField label="Task Name">
                      <input className="projects-input" value={taskDraft.name} onChange={(event) => setTaskDraft((prev) => ({ ...prev, name: event.target.value }))} required />
                    </ProjectField>
                    <ProjectField label="Status">
                      <select className="projects-input" value={taskDraft.status} onChange={(event) => setTaskDraft((prev) => ({ ...prev, status: event.target.value }))}>
                        {TASK_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{TASK_STATUS_LABELS[option]}</option>)}
                      </select>
                    </ProjectField>
                    <ProjectField label="Start Date">
                      <input className="projects-input" type="date" value={taskDraft.start_date} onChange={(event) => setTaskDraft((prev) => ({ ...prev, start_date: event.target.value }))} />
                    </ProjectField>
                    <ProjectField label="Due Date">
                      <input className="projects-input" type="date" value={taskDraft.end_date} onChange={(event) => setTaskDraft((prev) => ({ ...prev, end_date: event.target.value }))} />
                    </ProjectField>
                    <ProjectField label="Progress">
                      <input className="projects-input" type="number" min="0" max="100" value={taskDraft.progress_percent} onChange={(event) => setTaskDraft((prev) => ({ ...prev, progress_percent: Number(event.target.value || 0) }))} />
                    </ProjectField>
                    <ProjectField label="Notes">
                      <textarea className="projects-input projects-textarea" value={taskDraft.notes} onChange={(event) => setTaskDraft((prev) => ({ ...prev, notes: event.target.value }))} />
                    </ProjectField>
                  </div>
                  <div className="projects-form-actions">
                    <Button variant="ghost" onClick={() => setShowTaskForm(false)}>Cancel</Button>
                    <Button type="submit" disabled={saving}>Create Task</Button>
                  </div>
                </form>
              ) : null}

              {selectedTasks.length ? (
                <div className="projects-task-list">
                  {selectedTasks.map((task) => {
                    const progress = Number(task.progress_percent || 0);
                    const nextProgress = progress >= 100 ? 0 : Math.min(100, progress + 25);
                    return (
                      <div className="projects-task-item" key={task.id}>
                        <div className="projects-task-topline">
                          <strong>{task.name}</strong>
                          <span className={`projects-status-badge projects-status-badge--${String(task.status || '').toLowerCase()}`}>
                            {TASK_STATUS_LABELS[normalizeTaskStatus(task.status)] || task.status || 'Not Started'}
                          </span>
                        </div>
                        {(task.notes || task.description) ? (
                          <p className="projects-task-copy">{task.notes || task.description}</p>
                        ) : null}
                        <div className="projects-task-meta">
                          <span>{formatDate(task.start_date)}</span>
                          <span>{formatDate(task.end_date)}</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="projects-task-progress">
                          <div className="projects-task-progress-fill" style={{ width: `${progress}%` }} />
                        </div>
                        {canManageTasks ? (
                          <div className="projects-task-actions">
                            <Button size="sm" variant="secondary" onClick={() => handleTaskProgress(task, nextProgress)} disabled={saving}>
                              {progress >= 100 ? 'Reopen' : 'Advance'}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState title="No tasks yet" message="Create the first task to start structuring this project." />
              )}
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
